import { CustomValidationService } from './../../shared/services/validator/custom-validation.service';
import { FormGroup, Validators } from '@angular/forms';
import { FormBuilder } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { SupplierFileValidationEnum } from 'src/app/shared/enum/supplier-file-validation.enum';
import * as _ from 'lodash';
import { RegexEnum } from 'src/app/core/enum/regex.enum';
import { Observable } from 'rxjs';
import { HcPagination } from 'src/app/modals/hc-pagination.modal';
import { CountryAbbreviationEnum } from 'src/app/shared/enum/country-abbreviation.enum';

@Injectable({
  providedIn: 'root'
})
export class MassSuppliersService {
  public supplierApiUrl = environment.runtime.userManagementApiUrl;
  private _uploadedFile: File;

  /**
   * BatchId for current processing group in temp table
   */
  private _batchId: string;

  /**
   * Count for total suppliers in file
   */
  private _totalSuppliersCount: number;


  constructor(
    private _httpClient: HttpClient,
    private _fb: FormBuilder,
    public customValitor: CustomValidationService
    ) { }

  public setUploadedFile(file: File): void {
    this._uploadedFile = file;
  }

  public setTotalSuppliers(totalSuppliers: number): void {
    this._totalSuppliersCount = totalSuppliers;
  }

  public getTotalSuppliersCount(): number {
    return this._totalSuppliersCount;
  }

  public setBatchId(batchId: string): void {
    this._batchId = batchId;
  }

  /**
   * Check if file has expected extension and size(<5MB)
   */
  public validateFileHeader(): any {
    if (!this._uploadedFile) throw Error('File Not found.');

    const fileNameArr = this._uploadedFile.name.split('.');
    const type = fileNameArr[fileNameArr.length - 1];

    let result = { valid: true, message: '' };
    if (!this._uploadedFile) {
      result.valid = false;
      result.message = 'Error while loading file.';
    } else if (SupplierFileValidationEnum.SUPPORTED_FILE_TYPES.split(',').indexOf(type.toUpperCase()) === -1) {
      result.valid = false;
      result.message = 'File type doesn’t match. Please reupload with the correct template.';
    } else if (this._uploadedFile.size > Number(SupplierFileValidationEnum.MAX_FILE_SIZE)) {
      result.valid = false;
      result.message = 'File size exceeded.';
    }

    return result;
  }


  /**
   * Upload excel file to server and get back batchID
   */
  public validateSuppliers(): Observable<{}> {
    const data = new FormData();
    data.append('file', this._uploadedFile);
    return this._httpClient.post(`${this.supplierApiUrl}/supplier/mass-upload`, data);
  }

  /**
   * Get temp suppliers
   * @param page
   * @param status
   */
  public getSuppliers(page: HcPagination, status: string = 'PROCESSING'): Observable<{}> {
    if (!this._batchId) throw Error('BatchId not found');
    return this._httpClient.get(`${this.supplierApiUrl}/supplier/mass-upload/temp-suppliers/${this._batchId}?page=${page.getPageIndex()}&&pageSize=${page.getPageSize()}&&status=${status}`);
  }

  /**
   * Update Supplier information in temporary collection
   * @param supplier
   */
  public updateSupplier(supplier: any): Observable<{}> {
    return this._httpClient.put(`${this.supplierApiUrl}/supplier/mass-upload/update/${supplier.id}`, { batchID: this._batchId, supplierPayload: supplier });
  }

  /**
   * Confirm supplier is not duplicate
   * @param supplier
   */
  public confirmSupplier(supplier: any): Observable<{}> {
    return this._httpClient.put(`${this.supplierApiUrl}/supplier-mass/confirm-supplier/${supplier.id}`, { batchID: this._batchId, supplierPayload: supplier });
  }

  /**
   * Save validated suppliers to suppliers collection from temp collection
   */
  public submitValidSuppliers(): Observable<{}> {
    return this._httpClient.post(`${this.supplierApiUrl}/supplier/mass-upload/save/${this._batchId}`, {});
  }

  /**
   * Download invalid suppliers into excel sheet
   */
  public getDownloadInvalidSupplierLink(): Observable<Blob> {
    // This route returns an excel file to download. We wrap it in _httpClient and specify the return type as a blob. See review-suppliers.compontent.ts to see how to unpack and save the file.
    return this._httpClient.get(`${this.supplierApiUrl}/supplier/mass-upload/download-invalid-suppliers/${this._batchId}`, {responseType: 'blob'});
  }


  public checkExistingUser(emailToCheck): Observable<{}> {
    return this._httpClient.get(`${this.supplierApiUrl}/supplier-mass/unique-email/${emailToCheck}`, {});
  }


  /**
   * Get supplier form
   * @param supplier
   * @returns FormGroup
   */
  public getSupplierForm(supplier: any = {}): FormGroup {
    const allowedCountries = Array.from(
      Object.keys(CountryAbbreviationEnum).map(k => CountryAbbreviationEnum[k as any])
    );

    return this._fb.group({
      name: [_.get(supplier, 'name', null), [Validators.required]],
      id: [_.get(supplier, 'id', null)],
      location: this._fb.group({
        name: [_.get(supplier, 'location.name', null), [Validators.required]],
        address: [_.get(supplier, 'location.address', null), [Validators.required]],
        city: [_.get(supplier, 'location.city', null), [Validators.required]],
        state: [_.get(supplier, 'location.state', null), [Validators.required]],
        postalCode: [_.get(supplier, 'location.postalCode', null), [Validators.required, this.customValitor.validatePostalCode]],
        countryCode: [allowedCountries.indexOf(_.get(supplier, 'location.countryCode', null)) > -1 ? _.get(supplier, 'location.countryCode', null) : null, [Validators.required]],
      }),
      contacts: this._fb.array([
        this._fb.group({
          phone: [_.get(supplier, 'contacts[0].phone', null), [Validators.required, Validators.pattern(RegexEnum.PHONE)]],
          email: [_.get(supplier, 'contacts[0].email', null), [Validators.required, Validators.pattern(RegexEnum.EMAIL)], this.customValitor.validateUniqueEmail.bind(this.customValitor.self)],
          name: [_.get(supplier, 'contacts[0].name', null), [Validators.required, Validators.pattern(RegexEnum.NAME)]]
        })
      ])
    });
  }
}
